import { clients } from "../lib/https";
import { ConfigurationSettings } from "../models/configuration-settings";
import { FileMetadata } from "/features/chat/types/chat";

export interface TitleResponse {
  conversationTitle: string;
  conversation_id: string;
}

// Message Update Types
export interface MessageComment {
  id: string;
  content: string;
  userId: string;
  createdDate: string;
  lastUpdatedDate: string;
}

export interface MessageAttachment {
  fileId: string;
  fileName: string;
  extension: string;
  createdDate?: string;
  lastUpdatedDate?: string;
  size?: number;
}

export interface MessageReaction {
  emoji: string;
  userIds: string[];
}

export interface IntegrationSource {
  source?: string;
  folderIds?: string[];
  providerId?: string;
  connectionIds?: string[];
  resourceRefs?: Array<{
    nodeId: string;
    nodeType: string;
  }>;
}

export interface MessageQueryMetadata {
  searchOnline?: boolean;
  isRegenerate?: boolean;
  imageGeneration?: boolean;
  integrationSources?: IntegrationSource[];
  tool_ids?: string[];
  skill_ids?: string[];
}

export interface UpdateMessagePayload {
  conversationId: string;
  messageId: string;
  content: string;
  comments?: MessageComment[];
  attachments?: MessageAttachment[];
  parentIds?: string[];
  mentions?: string[];
  reactions?: Record<string, MessageReaction>;
  queryMetadata?: MessageQueryMetadata;
}

export interface UpdateMessageResponse {
  success: boolean;
  message?: string;
  id?: string;
  content?: string;
  type?: string;
  senderId?: string;
  comments?: any;
  attachments?: any;
  parentIds?: any;
  editHistory?: any;
  mentions?: any;
  reactions?: any;
  createdDate?: string;
  lastUpdatedDate?: string;
  metadata?: any;
  status?: any;
  originalMessageId?: string;
}


/**
 * Updates a collaboration message with new content, attachments, mentions, etc.
 *
 * @param {UpdateMessagePayload} payload - The update payload containing message details.
 * @param {ConfigurationSettings} config - The configuration settings containing API paths and blocksKey.
 * @returns {Promise<UpdateMessageResponse>} The response from the collaboration API.
 *
 * @throws {Error} If the request fails or the server returns an error.
 *
 * @example
 * const response = await updateMessage({
 *   conversationId: 'conv123',
 *   messageId: 'msg456',
 *   content: 'Updated message content',
 *   parentIds: ['parentMsgId'],
 * }, config);
 */
export const updateMessage = async (
  payload: UpdateMessagePayload,
  config: ConfigurationSettings
): Promise<UpdateMessageResponse> => {
  const baseUrl = `${config.paths.seliseAiBaseUrl}`;
  // Use collaboration path + /messages for the update endpoint
  const url = `${baseUrl}${config.paths.seliseAi.collaboration}/messages`;

  return clients.put<UpdateMessageResponse>(url, config.blocksKey, JSON.stringify(payload));
};

export const getSSEStream = async (messageBody: string, config: ConfigurationSettings) => {
  const baseUrl = `${config.paths.seliseAiBaseUrl}`;
  const url = `${baseUrl}${config.paths.seliseAi.chatStream}`;
  return clients.post(url, config.blocksKey, messageBody) as Promise<Response>;
}

export const getTitle2 = async (conversationId: string, config: ConfigurationSettings) => {
  const baseUrl = `${config.paths.seliseAiBaseUrl}`;
  const url = `${baseUrl}${config.paths.seliseAi.collaborationConversation}/${conversationId}/title/generate`;
  return clients.get(url, config.blocksKey) as Promise<TitleResponse>;
}

export const getSearchRelatedQuestionList = async (
  data: {
    conversationId: string;
    messageId: string;
    type: string;
    modelName: string;
  },
  config: ConfigurationSettings
) => {
  const payload = {
    ...data
  };

  const baseUrl = `${config.paths.seliseAiBaseUrl}`;
  const url = `${baseUrl}${config.paths.seliseAi.suggestions}`;
  return clients.post(url, config.blocksKey, JSON.stringify(payload));
};

const buildIntegrationSourcesPayload = (
  integrationSources?: IntegrationSource[]
): IntegrationSource[] => {
  return (integrationSources ?? []).map((integrationSource) => {
    /* const fileResourceRefs = integrationSource.resourceRefs?.filter(
      (resourceRef) => resourceRef.nodeType === 'file'
    );

    if (fileResourceRefs && fileResourceRefs.length > 0) {
      return {
        ...integrationSource,
        resourceRefs: fileResourceRefs,
      };
    } */

    const { resourceRefs, ...restIntegrationSource } = integrationSource;
    return restIntegrationSource;
  });
};

export const getGroupChatStream = async (
  messageBody: string,
  config: ConfigurationSettings,
  conversationId: string,
  attachmentsMetadata?: FileMetadata[],
  parentIds?: string[],
  mentions?: string[],
  isRegenerate?: boolean,
  imageGeneration?: boolean,
  integrationSources?: IntegrationSource[],
  searchOnline?: boolean,
  toolIds?: string[],
  skillIds?: string[],
  a2uiMetadata?: Record<string, unknown>,
) => {
  const payload = {
    conversationId,
    message: {
      content: messageBody,
      attachments: attachmentsMetadata || [],
      ...(parentIds && { parentIds }),
      ...(mentions && mentions.length > 0 && { mentions }),
      ...(a2uiMetadata && { metadata: { a2ui: a2uiMetadata } }),
      queryMetadata: {
        searchOnline: searchOnline ?? false,
        isRegenerate: isRegenerate ?? false,
        imageGeneration: imageGeneration ?? false,
        integrationSources: buildIntegrationSourcesPayload(integrationSources),
        tool_ids: toolIds ?? [],
        skill_ids: skillIds ?? [],
    }
    },

  };

  // console.log('attachments in getGroupChatStream', attachments,"payload:", payload)

  const baseUrl = `${config.paths.seliseAiBaseUrl}`;
  const url = `${baseUrl}${config.paths.seliseAi.collaboration}`;
  // const url = "http://localhost:8000/api/collaboration"
  return clients.post(url, config.blocksKey, JSON.stringify(payload)) as Promise<Response>;
}

export interface ResumeChatQueryMetadata {
  tool_ids?: string[];
  skill_ids?: string[];
  searchOnline?: boolean;
  imageGeneration?: boolean;
  integrationSources?: IntegrationSource[];
}

/**
 * Resumes a turn paused on `auth_required`. The BE reuses `assistantMessageId` (the paused
 * generation) and streams the answer under the original user message — no new user bubble.
 * With `approvalId` set it instead resolves a HIL approval interrupt (wire contract §3);
 * content/queryMetadata are omitted from that body.
 */
export const getResumeChatStream = async (
  payload: {
    conversationId: string;
    messageId: string;
    assistantMessageId: string;
    content?: string;
    queryMetadata?: ResumeChatQueryMetadata;
    approvalId?: string;
    decision?: 'approved' | 'declined';
    comment?: string;
    /** Grant breadth. Omitted (with grantTarget) for a plain approve, i.e. once. */
    scope?: 'once' | 'chat' | 'always';
    grantTarget?: 'action' | 'tool';
  },
  config: ConfigurationSettings
) => {
  const baseUrl = `${config.paths.seliseAiBaseUrl}`;
  const url = `${baseUrl}${config.paths.seliseAi.collaboration}/resume`;
  const base = {
    conversationId: payload.conversationId,
    messageId: payload.messageId,
    assistantMessageId: payload.assistantMessageId,
  };
  const body = payload.approvalId
    ? {
        ...base,
        approvalId: payload.approvalId,
        decision: payload.decision,
        ...(payload.comment && { comment: payload.comment }),
        ...(payload.scope && { scope: payload.scope }),
        ...(payload.grantTarget && { grantTarget: payload.grantTarget }),
      }
    : {
        ...base,
        content: payload.content ?? '',
        queryMetadata: {
          searchOnline: payload.queryMetadata?.searchOnline ?? false,
          imageGeneration: payload.queryMetadata?.imageGeneration ?? false,
          integrationSources: buildIntegrationSourcesPayload(payload.queryMetadata?.integrationSources),
          tool_ids: payload.queryMetadata?.tool_ids ?? [],
          skill_ids: payload.queryMetadata?.skill_ids ?? [],
        },
      };
  return clients.post(url, config.blocksKey, JSON.stringify(body)) as Promise<Response>;
};

export function buildCollaborationCancelPayload(
  conversationId: string,
  ids: { assistantMessageId?: string | null; userMessageId?: string | null }
): { conversationId: string; assistantMessageId: string } | { conversationId: string; userMessageId: string } | null {
  if (ids.assistantMessageId) {
    return { conversationId, assistantMessageId: ids.assistantMessageId };
  }
  if (ids.userMessageId) {
    return { conversationId, userMessageId: ids.userMessageId };
  }
  return null;
}

export const stopGeneration = async (
  payload: { conversationId: string; assistantMessageId?: string; userMessageId?: string },
  config: ConfigurationSettings
) => {
  const baseUrl = `${config.paths.seliseAiBaseUrl}`;
  const url = `${baseUrl}${config.paths.seliseAi.collaboration}/cancel`;
  return clients.post(url, config.blocksKey, JSON.stringify(payload)) as Promise<Response>;
}
